import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, ArrowLeft, Eye, EyeOff, User } from "lucide-react";
import ForgotPasswordDialog from "@/components/ForgotPasswordDialog";
import { TwoFactorDialog, ForcePasswordChangeDialog } from "@/components/LoginFlowDialogs";
import { checkSession } from "@/utils/auth";
import { useLoginFlow } from "@/hooks/useLoginFlow";

const AdminLogin = () => {
  const [username, setUsername] = useState("admin");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const navigate = useNavigate();
  const { handleLogin, loading, flow, setFlow, handleOtpVerified, handlePasswordChanged } = useLoginFlow("admin", "/admin/dashboard");

  useEffect(() => {
    checkSession().then(session => {
      if (session.authenticated && (session.roles.includes("admin") || session.is_master)) {
        navigate("/admin/dashboard");
      }
    });
  }, [navigate]);

  const onSubmit = (e) => { e.preventDefault(); handleLogin(username, password); };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: "radial-gradient(ellipse at center, #0A1910 0%, #000000 70%)" }}>
      <div className="max-w-md w-full">
        {/* Back Button */}
        <div className="mb-6">
          <Link to="/">
            <Button variant="ghost" className="text-white hover:bg-white/20">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>

        {/* Login Card */}
        <Card className="bg-white shadow-2xl border-none">
          <CardHeader className="text-center pb-4">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mx-auto mb-4">
              <Shield className="h-8 w-8 text-blue-600" />
            </div>
            <CardTitle className="text-3xl font-bold text-gray-800">
              Admin Login
            </CardTitle>
            <p className="text-gray-600 mt-2">Enter credentials to access admin panel</p>
          </CardHeader>
          <CardContent className="px-8 pb-8">
            <form onSubmit={onSubmit} className="space-y-6">
              {/* Username Input */}
              <div className="space-y-2">
                <Label htmlFor="username" className="text-lg font-semibold">
                  Username
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <Input
                    data-testid="admin-username-input"
                    id="username"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter username"
                    className="h-12 pl-12 text-lg border-2"
                    required
                  />
                </div>
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <Label htmlFor="password" className="text-lg font-semibold">
                  Password
                </Label>
                <div className="relative">
                  <Input
                    data-testid="password-input"
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter admin password"
                    className="h-12 pr-12 text-lg border-2"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              {/* Login Button */}
              <Button
                data-testid="login-button"
                type="submit"
                disabled={loading}
                className="w-full h-12 text-lg font-semibold bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg"
              >
                {loading ? "Logging in..." : "Login"}
              </Button>

              {/* Forgot Password Link */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => setShowForgotPassword(true)}
                  className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
                  data-testid="admin-forgot-password-link"
                >
                  Forgot Password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Forgot Password Dialog */}
      <ForgotPasswordDialog
        open={showForgotPassword}
        onOpenChange={setShowForgotPassword}
        loginType="admin"
      />

      {/* 2FA & Force-Password-Change Flows */}
      <TwoFactorDialog
        open={!!flow.twoFactor?.open}
        onOpenChange={(o) => setFlow((f) => ({ ...f, twoFactor: o ? f.twoFactor : null }))}
        role="admin"
        emailHint={flow.twoFactor?.emailHint}
        devOtp={flow.twoFactor?.devOtp}
        onVerified={handleOtpVerified}
      />
      <ForcePasswordChangeDialog
        open={!!flow.forcePwd?.open}
        onOpenChange={(o) => setFlow((f) => ({ ...f, forcePwd: o ? f.forcePwd : null }))}
        role="admin"
        currentPassword={flow.forcePwd?.currentPassword || ""}
        onChanged={handlePasswordChanged}
      />
    </div>
  );
};

export default AdminLogin;
